import { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { BUSINESS_TYPE_OPTIONS } from "../../config/businessLabels";
import type { BusinessType } from "../../types";
import styles from "./LoginPage.module.css";
import { Building2, Loader, XCircle } from "lucide-react";
import { PLATFORM_BRAND } from "../../config/platformBrand";
import YulqoMark from "../../components/ui/YulqoMark";

type ApiErrorBody = {
  code?: string;
  error?: string;
};

const REGISTER_ERROR_KEYS: Record<string, string> = {
  AUTH_EMAIL_EXISTS: "admin.register.emailExists",
  AUTH_FIELDS_REQUIRED: "admin.register.fieldsRequired",
  INTERNAL_ERROR: "admin.register.serverError",
};

const RegisterPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { register } = useAuth();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    organizationName: "",
    businessType: "OTHER" as BusinessType,
    phone: "",
  });

  const mapRegisterError = useCallback(
    (err: unknown): string => {
      const axiosErr = err as {
        response?: { status?: number; data?: ApiErrorBody | string };
      };
      const status = axiosErr.response?.status;
      const body = axiosErr.response?.data;
      if (status === 429) return t("admin.register.rateLimited");
      if (typeof body === "object" && body?.code && REGISTER_ERROR_KEYS[body.code]) {
        return t(REGISTER_ERROR_KEYS[body.code]);
      }
      if (!axiosErr.response) return t("admin.register.networkError");
      if (status && status >= 500) {
        return `${t("admin.register.serverError")} (${status})`;
      }
      return t("admin.register.error");
    },
    [t],
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    const fd = new FormData(e.currentTarget);
    const name = String(fd.get("name") ?? "").trim();
    const email = String(fd.get("email") ?? "").trim();
    const password = String(fd.get("password") ?? "");
    const organizationName = String(fd.get("organizationName") ?? "").trim();
    const phone = String(fd.get("phone") ?? "").trim();
    const businessTypeRaw = String(fd.get("businessType") ?? "");
    const businessType = BUSINESS_TYPE_OPTIONS.some(
      (option) => option.value === businessTypeRaw,
    )
      ? (businessTypeRaw as BusinessType)
      : formData.businessType;

    setFormData({
      name,
      email,
      password,
      organizationName,
      businessType,
      phone,
    });

    if (!name || !email || !password || !organizationName) {
      setError(t("admin.register.fieldsRequired"));
      return;
    }

    setLoading(true);

    try {
      await register({
        name,
        email,
        password,
        organizationName,
        businessType,
        phone: phone || undefined,
        language: (i18n.language || "es").split("-")[0],
      });
      navigate("/admin/onboarding");
    } catch (err: unknown) {
      setError(mapRegisterError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.loginBrand}>
        <YulqoMark size={36} />
        <span className={styles.loginWordmark}>{PLATFORM_BRAND.productName}</span>
      </div>
      <div className={styles.card}>
        <div className={styles.header}>
          <h1 className={styles.title}>
            <Building2 className={styles.titleIcon} aria-hidden="true" />
            {t("admin.register.title")}
          </h1>
          <p className={styles.subtitle}>{t("admin.register.subtitle")}</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div>
            <label className={styles.label} htmlFor="businessType">
              {t("admin.register.businessType")}
            </label>
            <select
              id="businessType"
              name="businessType"
              required
              value={formData.businessType}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  businessType: e.target.value as BusinessType,
                })
              }
              className={styles.input}
            >
              {BUSINESS_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.labelKey)} — {t(option.descriptionKey)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={styles.label} htmlFor="organizationName">
              {t("admin.register.organizationName")}
            </label>
            <input
              id="organizationName"
              name="organizationName"
              type="text"
              autoComplete="organization"
              required
              value={formData.organizationName}
              onChange={(e) =>
                setFormData({ ...formData, organizationName: e.target.value })
              }
              className={styles.input}
              placeholder={t("admin.register.organizationPlaceholder")}
            />
          </div>

          <div>
            <label className={styles.label} htmlFor="name">
              {t("admin.register.name")}
            </label>
            <input
              id="name"
              name="name"
              type="text"
              autoComplete="name"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={styles.input}
              placeholder={t("admin.register.namePlaceholder")}
            />
          </div>

          <div>
            <label className={styles.label} htmlFor="email">
              {t("admin.register.email")}
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className={styles.input}
              placeholder={t("admin.register.emailPlaceholder")}
            />
          </div>

          <div>
            <label className={styles.label} htmlFor="password">
              {t("admin.register.password")}
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={6}
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              className={styles.input}
              placeholder={t("admin.register.passwordPlaceholder")}
            />
          </div>

          <div>
            <label className={styles.label} htmlFor="phone">
              {t("admin.register.phone")}
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              autoComplete="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className={styles.input}
              placeholder={t("admin.register.phonePlaceholder")}
            />
          </div>

          {error && (
            <div className={styles.error}>
              <XCircle className={styles.iconSm} aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          <button type="submit" disabled={loading} className={styles.button}>
            {loading ? (
              <>
                <Loader className={styles.spin} aria-hidden="true" />
                {t("admin.register.creating")}
              </>
            ) : (
              t("admin.register.submit")
            )}
          </button>
        </form>

        <div className={styles.footer}>
          <p className={styles.subtitle}>
            {t("admin.register.hasAccount")}{" "}
            <Link to="/admin/login" className={styles.backLink}>
              {t("admin.register.login")}
            </Link>
          </p>
          <Link to="/" className={styles.backLink}>
            ← {t("common.back")}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
